import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CarOutlined,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  EuroOutlined,
  EyeOutlined,
  InboxOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FulfillmentMethod,
  OrderDto,
  OrderStatus,
  PaymentStatus,
  useDeleteOrder,
  useOrders,
  useOrdersStats,
  useUpdateOrderStatus,
} from '@shared/api/hooks/useOrders';
import { RefundModal } from './components/RefundModal';
import { ScanQrModal } from './components/ScanQrModal';
import { OrderDetailDrawer } from './components/OrderDetailDrawer';

const STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING: 'In attesa',
  CONFIRMED: 'Confermato',
  READY_FOR_PICKUP: 'Pronto al ritiro',
  SHIPPED: 'Spedito',
  DELIVERED: 'Consegnato',
  PICKED_UP: 'Ritirato',
  CANCELLED: 'Annullato',
  REFUNDED: 'Rimborsato',
};

const STATUS_COLORS: Record<OrderStatus, string> = {
  PENDING: 'gold',
  CONFIRMED: 'blue',
  READY_FOR_PICKUP: 'cyan',
  SHIPPED: 'geekblue',
  DELIVERED: 'green',
  PICKED_UP: 'green',
  CANCELLED: 'volcano',
  REFUNDED: 'red',
};

const PAYMENT_LABELS: Record<PaymentStatus, string> = {
  PENDING: 'Da pagare',
  REQUIRES_ACTION: '3DS in corso',
  PAID: 'Pagato',
  FAILED: 'Fallito',
  REFUNDED: 'Rimborsato',
  PARTIALLY_REFUNDED: 'Rimborso parz.',
};

const PAYMENT_COLORS: Record<PaymentStatus, string> = {
  PENDING: 'orange',
  REQUIRES_ACTION: 'gold',
  PAID: 'green',
  FAILED: 'red',
  REFUNDED: 'red',
  PARTIALLY_REFUNDED: 'volcano',
};

function nextStatus(
  status: OrderStatus,
  fulfillment: FulfillmentMethod,
): OrderStatus | null {
  switch (status) {
    case 'PENDING':
      return 'CONFIRMED';
    case 'CONFIRMED':
      return fulfillment === 'SHIPPING' ? 'SHIPPED' : 'READY_FOR_PICKUP';
    case 'READY_FOR_PICKUP':
      return 'PICKED_UP';
    case 'SHIPPED':
      return 'DELIVERED';
    default:
      return null;
  }
}

type TabKey = 'pickup_today' | 'to_ship' | 'in_transit' | 'all' | 'refund';

const TAB_FILTERS: Record<
  TabKey,
  { status?: OrderStatus; paymentStatus?: PaymentStatus; fulfillmentMethod?: FulfillmentMethod }
> = {
  pickup_today: { status: 'READY_FOR_PICKUP' },
  to_ship: { status: 'CONFIRMED', fulfillmentMethod: 'SHIPPING' },
  in_transit: { status: 'SHIPPED' },
  all: {},
  refund: { paymentStatus: 'REFUNDED' },
};

export function OrdersPage(): React.ReactElement {
  const [tab, setTab] = useState<TabKey>('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | undefined>();
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | undefined>();
  const [drawerOrder, setDrawerOrder] = useState<OrderDto | null>(null);
  const [refundOrder, setRefundOrder] = useState<OrderDto | null>(null);
  const [scanOpen, setScanOpen] = useState(false);

  const baseFilter = TAB_FILTERS[tab];
  const filters = useMemo(
    () => ({
      page,
      limit: 20,
      status: statusFilter ?? baseFilter.status,
      paymentStatus: paymentFilter ?? baseFilter.paymentStatus,
      fulfillmentMethod: baseFilter.fulfillmentMethod,
      search: search.trim() || undefined,
    }),
    [page, statusFilter, paymentFilter, baseFilter, search],
  );

  const { data: pageData, isLoading, refetch, isFetching } = useOrders(filters);
  const { data: stats, isLoading: statsLoading } = useOrdersStats();
  const updateStatusMutation = useUpdateOrderStatus();
  const deleteMutation = useDeleteOrder();

  const data = pageData?.data ?? [];
  const meta = pageData?.meta;

  const onAdvance = async (o: OrderDto): Promise<void> => {
    const next = nextStatus(o.status, o.fulfillmentMethod);
    if (!next) return;
    try {
      await updateStatusMutation.mutateAsync({ id: o.id, status: next });
      message.success(`Ordine → ${STATUS_LABELS[next]}`);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onCancel = async (o: OrderDto): Promise<void> => {
    try {
      await updateStatusMutation.mutateAsync({ id: o.id, status: 'CANCELLED' });
      message.success('Ordine annullato (restock automatico se applicabile)');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onDelete = async (o: OrderDto): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(o.id);
      message.success('Ordine eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onScanMatch = (o: OrderDto): void => {
    setScanOpen(false);
    setDrawerOrder(o);
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Ordini
          </Typography.Title>
          <Tag>{meta?.total ?? data.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Input
            placeholder="Cerca QR (es. ORD-TER-...)"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            allowClear
            style={{ width: 260 }}
          />
          <Button
            icon={<QrcodeOutlined />}
            onClick={() => setScanOpen(true)}
            type="primary"
            ghost
          >
            Scan QR
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
        </Space>
      }
    >
      {/* T-119 — mini-dashboard: i numeri che contano per chi gestisce gli ordini,
          calcolati sull'intero set (endpoint /stats), non sulla pagina. A zero
          ordini restano 0 / 0.00 € (nessun NaN, nessun riquadro vuoto). Le carte
          azionabili portano alla tab/filtro corrispondente. */}
      {(() => {
        const bs = stats?.byStatus;
        const pending = bs?.PENDING ?? 0;
        const confirmed = bs?.CONFIRMED ?? 0;
        const confirmedShipping = stats?.confirmedShipping ?? 0;
        const readyPickup = bs?.READY_FOR_PICKUP ?? 0;
        const inTransit = bs?.SHIPPED ?? 0;
        const toEvade = pending + confirmed;
        const pickupToPrepare = Math.max(confirmed - confirmedShipping, 0);
        const currency = stats?.revenue.currency ?? 'EUR';
        const netCollected = stats?.revenue.netCollected ?? 0;
        const refunded = stats?.revenue.refunded ?? 0;
        const jumpTo = (t: TabKey, payment?: PaymentStatus): void => {
          setTab(t);
          setPage(1);
          setStatusFilter(undefined);
          setPaymentFilter(payment);
        };
        const cardBody = { padding: 16 } as const;
        return (
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col flex="1 1 190px">
              <Card size="small" styles={{ body: cardBody }}>
                <Statistic
                  loading={statsLoading}
                  title="Da evadere"
                  value={toEvade}
                  prefix={<InboxOutlined />}
                  valueStyle={{ color: toEvade > 0 ? '#d46b08' : undefined }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {pending} da confermare · {confirmedShipping} da spedire · {pickupToPrepare} ritiri
                  da preparare
                </Typography.Text>
              </Card>
            </Col>
            <Col flex="1 1 190px">
              <Card
                size="small"
                hoverable
                styles={{ body: cardBody }}
                onClick={() => jumpTo('pickup_today')}
              >
                <Statistic
                  loading={statsLoading}
                  title="Pronti al ritiro"
                  value={readyPickup}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: readyPickup > 0 ? '#08979c' : undefined }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  in attesa del cliente
                </Typography.Text>
              </Card>
            </Col>
            <Col flex="1 1 190px">
              <Card
                size="small"
                hoverable
                styles={{ body: cardBody }}
                onClick={() => jumpTo('in_transit')}
              >
                <Statistic
                  loading={statsLoading}
                  title="In transito"
                  value={inTransit}
                  prefix={<CarOutlined />}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  spediti, non consegnati
                </Typography.Text>
              </Card>
            </Col>
            <Col flex="1 1 190px">
              <Card
                size="small"
                hoverable
                styles={{ body: cardBody }}
                onClick={() => jumpTo('all', 'PENDING')}
              >
                <Statistic
                  loading={statsLoading}
                  title="Da pagare"
                  value={stats?.paymentPending ?? 0}
                  prefix={<WalletOutlined />}
                  valueStyle={{ color: (stats?.paymentPending ?? 0) > 0 ? '#d4380d' : undefined }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  pagamento non ancora saldato
                </Typography.Text>
              </Card>
            </Col>
            <Col flex="1 1 220px">
              <Card size="small" styles={{ body: cardBody }}>
                <Statistic
                  loading={statsLoading}
                  title="Incasso netto"
                  value={netCollected}
                  precision={2}
                  prefix={<EuroOutlined />}
                  suffix={currency}
                />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  ultime 24h: {stats?.last24h.count ?? 0} ordini
                  {refunded > 0 ? ` · rimborsato −${refunded.toFixed(2)} ${currency}` : ''}
                </Typography.Text>
              </Card>
            </Col>
          </Row>
        );
      })()}

      <Tabs
        activeKey={tab}
        onChange={(k) => {
          setTab(k as TabKey);
          setPage(1);
          setStatusFilter(undefined);
          setPaymentFilter(undefined);
        }}
        items={[
          { key: 'all', label: 'Tutti' },
          { key: 'pickup_today', label: 'Pickup oggi' },
          { key: 'to_ship', label: 'Da spedire' },
          { key: 'in_transit', label: 'In transito' },
          { key: 'refund', label: 'Rimborsati' },
        ]}
      />

      <Space style={{ marginBottom: 12 }}>
        <Select
          placeholder="Stato"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v as OrderStatus | undefined);
            setPage(1);
          }}
          options={(Object.keys(STATUS_LABELS) as OrderStatus[]).map((s) => ({
            value: s,
            label: STATUS_LABELS[s],
          }))}
        />
        <Select
          placeholder="Pagamento"
          allowClear
          style={{ width: 180 }}
          value={paymentFilter}
          onChange={(v) => {
            setPaymentFilter(v as PaymentStatus | undefined);
            setPage(1);
          }}
          options={(Object.keys(PAYMENT_LABELS) as PaymentStatus[]).map((s) => ({
            value: s,
            label: PAYMENT_LABELS[s],
          }))}
        />
      </Space>

      <Table<OrderDto>
        rowKey="id"
        loading={isLoading}
        dataSource={data}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        onRow={(r) => ({
          onClick: () => setDrawerOrder(r),
          style: { cursor: 'pointer' },
        })}
        columns={[
          {
            title: 'QR',
            dataIndex: 'pickupQrCode',
            width: 200,
            responsive: ['lg'],
            render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
          },
          {
            title: 'Data',
            dataIndex: 'createdAt',
            width: 140,
            render: (v: string) => dayjs(v).format('DD/MM HH:mm'),
          },
          {
            title: 'Cliente',
            width: 220,
            render: (_v, r) =>
              r.customer ? (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {[r.customer.firstName, r.customer.lastName].filter(Boolean).join(' ') ||
                      '—'}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {r.customer.email}
                  </Typography.Text>
                </Space>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: 'Tipo',
            dataIndex: 'fulfillmentMethod',
            width: 110,
            responsive: ['md'],
            render: (v: FulfillmentMethod) =>
              v === 'SHIPPING' ? <Tag>Spedizione</Tag> : <Tag color="cyan">Ritiro</Tag>,
          },
          {
            title: 'Items',
            width: 70,
            align: 'center',
            responsive: ['md'],
            render: (_v, r) => <Tag>{r.items.reduce((s, it) => s + it.quantity, 0)}</Tag>,
          },
          {
            title: 'Totale',
            width: 130,
            align: 'right',
            render: (_v, r) => (
              <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                <Typography.Text strong>
                  {r.finalAmount.toFixed(2)} {r.currency}
                </Typography.Text>
                {r.refundedAmount > 0 ? (
                  <Typography.Text type="danger" style={{ fontSize: 11 }}>
                    −{r.refundedAmount.toFixed(2)}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: 'Stato',
            dataIndex: 'status',
            width: 150,
            render: (s: OrderStatus) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>,
          },
          {
            title: 'Pagamento',
            dataIndex: 'paymentStatus',
            width: 140,
            render: (s: PaymentStatus) => (
              <Tag color={PAYMENT_COLORS[s]}>{PAYMENT_LABELS[s]}</Tag>
            ),
          },
          {
            title: 'Azioni',
            width: 220,
            align: 'right',
            render: (_v, r) => {
              const next = nextStatus(r.status, r.fulfillmentMethod);
              const cancellable =
                r.status === 'PENDING' ||
                r.status === 'CONFIRMED' ||
                r.status === 'READY_FOR_PICKUP' ||
                r.status === 'SHIPPED';
              const refundable =
                r.paymentIntentId != null && r.paymentStatus !== 'REFUNDED';
              const deletable =
                r.status === 'CANCELLED' ||
                r.status === 'REFUNDED' ||
                r.paymentStatus === 'PENDING' ||
                r.paymentStatus === 'FAILED';
              return (
                <Space
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  wrap
                  style={{ justifyContent: 'flex-end' }}
                >
                  <Tooltip title="Dettagli">
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => setDrawerOrder(r)}
                    />
                  </Tooltip>
                  {next ? (
                    <Tooltip title={`Avanza a ${STATUS_LABELS[next]}`}>
                      <Popconfirm
                        title={`Avanzare a ${STATUS_LABELS[next]}?`}
                        okText="Conferma"
                        cancelText="Annulla"
                        onConfirm={() => onAdvance(r)}
                      >
                        <Button
                          type="primary"
                          size="small"
                          icon={<CheckOutlined />}
                          loading={updateStatusMutation.isPending}
                        />
                      </Popconfirm>
                    </Tooltip>
                  ) : null}
                  {refundable && (
                    <Tooltip title="Rimborsa">
                      <Button
                        size="small"
                        danger
                        ghost
                        icon={<RollbackOutlined />}
                        onClick={() => setRefundOrder(r)}
                      />
                    </Tooltip>
                  )}
                  {cancellable && (
                    <Tooltip title="Annulla ordine">
                      <Popconfirm
                        title="Annullare ordine? (restock automatico se non spedito)"
                        okText="Annulla ordine"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onCancel(r)}
                      >
                        <Button danger size="small" icon={<CloseOutlined />} />
                      </Popconfirm>
                    </Tooltip>
                  )}
                  {deletable && (
                    <Tooltip title="Elimina ordine">
                      <Popconfirm
                        title="Eliminare definitivamente questo ordine?"
                        okText="Elimina"
                        cancelText="No"
                        okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
                        onConfirm={() => onDelete(r)}
                      >
                        <Button danger size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Tooltip>
                  )}
                </Space>
              );
            },
          },
        ]}
      />

      <OrderDetailDrawer
        order={drawerOrder}
        open={drawerOrder != null}
        onClose={() => setDrawerOrder(null)}
        onRefund={(o) => {
          setDrawerOrder(null);
          setRefundOrder(o);
        }}
        statusLabel={STATUS_LABELS}
        statusColor={STATUS_COLORS}
        paymentLabel={PAYMENT_LABELS}
        paymentColor={PAYMENT_COLORS}
      />
      <RefundModal
        order={refundOrder}
        open={refundOrder != null}
        onClose={() => setRefundOrder(null)}
      />
      <ScanQrModal
        open={scanOpen}
        orders={data}
        onClose={() => setScanOpen(false)}
        onMatch={onScanMatch}
      />
    </Card>
  );
}
